import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Tooltip } from '@mui/material';
import {
  FilterList as FilterIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  HelpOutline as HelpIcon,
  CheckCircle as StatusIcon,
  Category as ResultViaIcon,
  Star as PriorityIcon
} from '@mui/icons-material';
import { AllocationFilter } from '../../types';

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const resultViaOptions = [
  { value: 'all', label: 'All' },
  { value: 'Partner', label: 'Partner' },
  { value: 'Internal', label: 'Internal' },
  { value: 'Courier', label: 'Courier' },
  { value: 'InternalWarehouse', label: 'Internal Warehouse' },
];

const priorityOptions = [
  { value: 'all', label: 'All' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

interface AllocationFiltersProps {
  filters: AllocationFilter;
  onFilterChange: (key: keyof AllocationFilter, value: any) => void;
  onReset: () => void;
}

export default function AllocationFilters({
  filters,
  onFilterChange,
  onReset,
}: AllocationFiltersProps) {
  const [debouncedSearch, setDebouncedSearch] = useState(filters.searchText || '');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.searchText || '');
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.searchText]);

  useEffect(() => {
    const currentSearch = filters.searchText || '';
    if (debouncedSearch !== currentSearch) {
      onFilterChange('searchText', debouncedSearch || undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleFilterChange = (key: keyof AllocationFilter, value: any) => {
    onFilterChange(key, value);
  };

  const clearAllFilters = () => {
    if (onReset) {
      onReset();
    } else {
      onFilterChange('status', 'all');
      onFilterChange('resultVia', 'all');
      onFilterChange('priority', 'all');
      onFilterChange('searchText', '');
    }
  };

  const hasActiveFilters =
    filters.status !== 'all' ||
    filters.resultVia !== 'all' ||
    filters.priority !== 'all' ||
    (filters.searchText && filters.searchText.trim() !== '');

  const getStatusLabel = (value: string) => {
    const option = statusOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const getResultViaLabel = (value: string) => {
    const option = resultViaOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const getPriorityLabel = (value: string) => {
    const option = priorityOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  return (
    <div className="bg-gradient-to-r from-white to-gray-50/30 rounded-xl border border-gray-200/60 shadow-sm p-5 mb-6">
      <div className="flex items-center flex-wrap gap-4">
        <div className="flex items-center gap-2 mr-2 bg-black/5 px-2.5 py-1.5 rounded-md">
          <FilterIcon sx={{ fontSize: '18px', color: '#374151' }} />
          <span className="text-sm font-bold text-gray-700">Filters</span>
          <Tooltip
            title="Use filters to narrow down rules. Active filters appear as removable tags."
            arrow
            placement="top"
          >
            <HelpIcon sx={{ fontSize: '15px', color: '#6B7280', cursor: 'help' }} />
          </Tooltip>
        </div>

        <Tooltip title="Search rules" arrow placement="top">
          <div className="relative">
            <SearchIcon sx={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '18px', color: '#9CA3AF' }} />
            <input
              type="text"
              value={debouncedSearch}
              onChange={(e) => setDebouncedSearch(e.target.value)}
              placeholder="Search rules..."
              className="pl-10 pr-4 py-2 text-sm border border-gray-300/80 rounded-lg focus:ring-2 focus:ring-primaryColor/30 focus:border-primaryColor outline-none w-48 bg-white/90 hover:bg-white transition-all shadow-sm hover:shadow"
            />
          </div>
        </Tooltip>

        <Tooltip title="Filter by status" arrow placement="top">
          <Autocomplete
            options={statusOptions}
            getOptionLabel={(option) => option.label || ''}
            value={statusOptions.find(opt => opt.value === filters.status) || statusOptions[0]}
            onChange={(event, newValue) => {
              handleFilterChange('status', newValue ? newValue.value : 'all');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="All Status"
                size="small"
                sx={{
                  minWidth: 150,
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#D1D5DB',
                      borderRadius: '0.5rem',
                    },
                    '&:hover fieldset': {
                      borderColor: '#9CA3AF',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3B82F6',
                      borderWidth: '1px',
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.value} className="px-4 py-2 hover:bg-gray-50">
                <span className="text-sm">{option.label}</span>
              </li>
            )}
            noOptionsText="No status found"
          />
        </Tooltip>

        <Tooltip title="Filter by result channel" arrow placement="top">
          <Autocomplete
            options={resultViaOptions}
            getOptionLabel={(option) => option.label || ''}
            value={resultViaOptions.find(opt => opt.value === filters.resultVia) || resultViaOptions[0]}
            onChange={(event, newValue) => {
              handleFilterChange('resultVia', newValue ? newValue.value : 'all');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="All Channels"
                size="small"
                sx={{
                  minWidth: 180,
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#D1D5DB',
                      borderRadius: '0.5rem',
                    },
                    '&:hover fieldset': {
                      borderColor: '#9CA3AF',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3B82F6',
                      borderWidth: '1px',
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.value} className="px-4 py-2 hover:bg-gray-50">
                <span className="text-sm">{option.label}</span>
              </li>
            )}
            noOptionsText="No channels found"
          />
        </Tooltip>

        <Tooltip title="Filter by priority" arrow placement="top">
          <Autocomplete
            options={priorityOptions}
            getOptionLabel={(option) => option.label || ''}
            value={priorityOptions.find(opt => opt.value === filters.priority) || priorityOptions[0]}
            onChange={(event, newValue) => {
              handleFilterChange('priority', newValue ? newValue.value : 'all');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="All Priorities"
                size="small"
                sx={{
                  minWidth: 150,
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#D1D5DB',
                      borderRadius: '0.5rem',
                    },
                    '&:hover fieldset': {
                      borderColor: '#9CA3AF',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3B82F6',
                      borderWidth: '1px',
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.value} className="px-4 py-2 hover:bg-gray-50">
                <span className="text-sm">{option.label}</span>
              </li>
            )}
            noOptionsText="No priority found"
          />
        </Tooltip>

        {filters.status && filters.status !== 'all' && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
            <StatusIcon sx={{ fontSize: '16px' }} />
            <span>{getStatusLabel(filters.status)}</span>
            <Tooltip title="Remove this filter" arrow>
              <button
                onClick={() => handleFilterChange('status', 'all')}
                className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
              >
                <CloseIcon sx={{ fontSize: '16px' }} />
              </button>
            </Tooltip>
          </div>
        )}

        {filters.resultVia && filters.resultVia !== 'all' && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
            <ResultViaIcon sx={{ fontSize: '16px' }} />
            <span>{getResultViaLabel(filters.resultVia)}</span>
            <Tooltip title="Remove this filter" arrow>
              <button
                onClick={() => handleFilterChange('resultVia', 'all')}
                className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
              >
                <CloseIcon sx={{ fontSize: '16px' }} />
              </button>
            </Tooltip>
          </div>
        )}

        {filters.priority && filters.priority !== 'all' && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
            <PriorityIcon sx={{ fontSize: '16px' }} />
            <span>{getPriorityLabel(filters.priority)}</span>
            <Tooltip title="Remove this filter" arrow>
              <button
                onClick={() => handleFilterChange('priority', 'all')}
                className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
              >
                <CloseIcon sx={{ fontSize: '16px' }} />
              </button>
            </Tooltip>
          </div>
        )}

        {filters.searchText && filters.searchText.trim() !== '' && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
            <SearchIcon sx={{ fontSize: '16px' }} />
            <span>{filters.searchText}</span>
            <Tooltip title="Remove this filter" arrow>
              <button
                onClick={() => handleFilterChange('searchText', '')}
                className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
              >
                <CloseIcon sx={{ fontSize: '16px' }} />
              </button>
            </Tooltip>
          </div>
        )}

        {hasActiveFilters && (
          <Tooltip title="Remove all active filters" arrow placement="top">
            <button
              onClick={clearAllFilters}
              className="ml-auto px-3 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium hover:bg-gray-100/80 rounded-lg transition-all shadow-sm hover:shadow"
            >
              Clear All
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
